import React, { useState } from "react";
import {
  Grid,
  Box,
  FormHelperText,
  Container,
  Paper,
  Typography,
  Button,
  IconButton,
  TextField,
  InputAdornment,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useNavigate } from "react-router-dom";
import theme from "Theme";
import ISelect from "../../components/SelectField";
import indianCities from "../../components/indianCities";
import { useCreateInterestedCaregiverMutation } from "services/caregiverApi";
import { useLoading } from "../../components/LoadingProvider";
import { useSnackbar } from "../../components/SnackbarProvider";
import { CaregiverStatuses } from "CommonData/Common";

const commonTextFieldProps = {
  fullWidth: true,
  variant: "outlined",
  InputProps: { style: { height: "56px" } },
};

const CaregiverApplyForm = () => {
  const navigate = useNavigate();
  const showSnackbar = useSnackbar();
  const { startLoading, stopLoading } = useLoading();
  const [createInteretedCG] = useCreateInterestedCaregiverMutation();

  const initialFormValue = {
    firstName: "",
    lastName: "",
    email: "",
    contactNumber: "",
    preferredLocation: "",
    resumeFile: null,
    comments: "",
  };

  const initialFormError = {
    firstName: "",
    lastName: "",
    email: "",
    contactNumber: "",
    preferredLocation: "",
    comments: "",
    resumeFile: "",
  };

  const [formValues, setFormValues] = useState(initialFormValue);
  const [formErrors, setFormErrors] = useState(initialFormError);

  // Validation functions
  const validateName = (name) => {
    // Allows letters, spaces, hyphens, and apostrophes
    const nameRegex = /^[A-Za-z\s'-]+$/;
    if (!name.trim()) return "This field is required";
    if (!nameRegex.test(name))
      return "Only letters, spaces, hyphens(-) and apostrophes(') are allowed";
    if (name.length < 2) return "Must be at least 2 characters";
    if (name.length > 50) return "Cannot exceed 50 characters";
    return "";
  };

  const validateEmail = (email) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!email.trim()) return "Email is required";
    if (!emailRegex.test(email)) return "Enter a valid email address";
    if (email.length > 100) return "Email cannot exceed 100 characters";
    return "";
  };

  const validateMobileNumber = (number) => {
    const mobileRegex = /^[6-9]\d{9}$/; // Indian mobile numbers start with 6-9
    if (!number) return "Mobile number is required";
    if (!mobileRegex.test(number))
      return "Enter a valid 10-digit Indian mobile number";
    return "";
  };

  const validateLocation = (location) => {
    if (!location) return "Preferred location is required";
    return "";
  };

  const validateComments = (comments) => {
    if (!comments.trim()) return ""; // Optional field

    // Allows alphanumeric, spaces, and common punctuation
    const commentRegex = /^[a-zA-Z0-9\s.,!?@#$%^&*()_+={}|[\]\\:;"'<>`~/-]*$/;
    if (!commentRegex.test(comments)) return "Contains invalid characters";
    if (comments.length > 500) return "Comments cannot exceed 500 characters";
    return "";
  };

  const validateResumeFile = (file) => {
    if (!file) return ""; // Optional field

    const allowedTypes = [
      "application/pdf",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];
    const maxSize = 5 * 1024 * 1024; // 5MB

    if (!allowedTypes.includes(file.type)) {
      return "Only PDF and DOCX files are allowed";
    }
    if (file.size > maxSize) {
      return "File size must be less than 5MB";
    }
    return "";
  };

  const handleInputChange = (name, value) => {
    setFormValues((prev) => ({ ...prev, [name]: value }));

    // Validate on change for immediate feedback
    let error = "";
    switch (name) {
      case "firstName":
        error = validateName(value);
        break;
      case "lastName":
        error = validateName(value);
        break;
      case "email":
        error = validateEmail(value);
        break;
      case "contactNumber":
        error = validateMobileNumber(value);
        break;
      case "preferredLocation":
        error = validateLocation(value);
        break;
      case "comments":
        error = validateComments(value);
        break;
      case "resumeFile":
        error = validateResumeFile(value);
        break;
    }

    setFormErrors((prev) => ({ ...prev, [name]: error }));
  };

  const handleNameInputChange = (name, value) => {
    // Only allow letters, spaces, hyphens, and apostrophes
    const filteredValue = value.replace(/[^A-Za-z\s'-]/g, "");
    handleInputChange(name, filteredValue);
  };

  const handleContactNumberChange = (name, value) => {
    // Only allow digits and limit to 10
    const numeric = value.replace(/[^0-9]/g, "");
    if (numeric.length <= 10) {
      handleInputChange(name, numeric);
    }
  };

  const handleEmailChange = (name, value) => {
    handleInputChange(name, value.toLowerCase()); // Convert email to lowercase
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    handleInputChange("resumeFile", file);
  };

  const validateForm = () => {
    const errors = {
      firstName: validateName(formValues.firstName),
      lastName: validateName(formValues.lastName),
      email: validateEmail(formValues.email),
      contactNumber: validateMobileNumber(formValues.contactNumber),
      preferredLocation: validateLocation(formValues.preferredLocation),
      comments: validateComments(formValues.comments),
      resumeFile: validateResumeFile(formValues.resumeFile),
    };

    setFormErrors(errors);
    return !Object.values(errors).some((error) => error !== "");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      showSnackbar("Please fix the errors in the form", "error");
      return;
    }

    try {
      startLoading();

      const formData = new FormData();
      formData.append("firstName", formValues.firstName.trim());
      formData.append("lastName", formValues.lastName.trim());
      formData.append("email", formValues.email.trim().toLowerCase());
      formData.append("contactNumber", "+91-" + formValues.contactNumber);
      formData.append("preferredLocation", formValues.preferredLocation);
      formData.append("comments", formValues.comments.trim());
      formData.append("status", CaregiverStatuses.INTERESTED.name);

      if (formValues.resumeFile) {
        formData.append("resumeFile", formValues.resumeFile);
      }

      await createInteretedCG(formData).unwrap();
      showSnackbar("Application submitted successfully", "success");
      handleClose();
    } catch (error) {
      showSnackbar(
        error?.data?.message || "Submission failed. Please try again",
        "error",
      );
    } finally {
      stopLoading();
    }
  };

  const handleClose = () => {
    setFormValues(initialFormValue);
    setFormErrors(initialFormError);
    navigate("/");
  };

  return (
    <Container maxWidth="md">
      <Box position="relative">
        <Paper sx={{ p: { xs: 2, sm: 4 }, borderRadius: 5 }}>
          <Typography variant="h5" gutterBottom>
            Caregiver Application
          </Typography>

          <IconButton
            onClick={handleClose}
            sx={{ position: "absolute", top: 10, right: 10, color: "red" }}
          >
            <CloseIcon />
          </IconButton>

          <form onSubmit={handleSubmit} noValidate>
            <Grid container spacing={3}>
              {/* First Name Field */}
              <Grid item xs={12} sm={6}>
                <TextField
                  {...commonTextFieldProps}
                  label="First Name *"
                  placeholder="Enter your first name"
                  value={formValues.firstName}
                  onChange={(e) =>
                    handleNameInputChange("firstName", e.target.value)
                  }
                  error={!!formErrors.firstName}
                  InputProps={{
                    endAdornment:
                      formValues.firstName && !formErrors.firstName ? (
                        <InputAdornment position="end">
                          <Tooltip title="Valid name">
                            <InfoOutlinedIcon
                              color="success"
                              fontSize="small"
                            />
                          </Tooltip>
                        </InputAdornment>
                      ) : null,
                  }}
                />
                {formErrors.firstName && (
                  <FormHelperText error>{formErrors.firstName}</FormHelperText>
                )}
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mt: 0.5 }}
                ></Typography>
              </Grid>

              {/* Last Name Field */}
              <Grid item xs={12} sm={6}>
                <TextField
                  {...commonTextFieldProps}
                  label="Last Name *"
                  placeholder="Enter your last name"
                  value={formValues.lastName}
                  onChange={(e) =>
                    handleNameInputChange("lastName", e.target.value)
                  }
                  error={!!formErrors.lastName}
                  InputProps={{
                    endAdornment:
                      formValues.lastName && !formErrors.lastName ? (
                        <InputAdornment position="end">
                          <Tooltip title="Valid name">
                            <InfoOutlinedIcon
                              color="success"
                              fontSize="small"
                            />
                          </Tooltip>
                        </InputAdornment>
                      ) : null,
                  }}
                />
                {formErrors.lastName && (
                  <FormHelperText error>{formErrors.lastName}</FormHelperText>
                )}
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mt: 0.5 }}
                ></Typography>
              </Grid>

              {/* Email Field */}
              <Grid item xs={12} sm={6}>
                <TextField
                  {...commonTextFieldProps}
                  label="Email *"
                  type="email"
                  placeholder="example@email.com"
                  value={formValues.email}
                  onChange={(e) => handleEmailChange("email", e.target.value)}
                  error={!!formErrors.email}
                  InputProps={{
                    endAdornment:
                      formValues.email && !formErrors.email ? (
                        <InputAdornment position="end">
                          <Tooltip title="Valid email">
                            <InfoOutlinedIcon
                              color="success"
                              fontSize="small"
                            />
                          </Tooltip>
                        </InputAdornment>
                      ) : null,
                  }}
                />
                {formErrors.email && (
                  <FormHelperText error>{formErrors.email}</FormHelperText>
                )}
              </Grid>

              {/* Contact Number Field */}
              <Grid item xs={12} sm={6}>
                <TextField
                  {...commonTextFieldProps}
                  label="Contact Number *"
                  placeholder="9876543210"
                  value={formValues.contactNumber}
                  onChange={(e) =>
                    handleContactNumberChange("contactNumber", e.target.value)
                  }
                  error={!!formErrors.contactNumber}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Typography sx={{ minWidth: 40, color: "gray" }}>
                          +91
                        </Typography>
                      </InputAdornment>
                    ),
                    endAdornment:
                      formValues.contactNumber && !formErrors.contactNumber ? (
                        <InputAdornment position="end">
                          <Tooltip title="Valid mobile number">
                            <InfoOutlinedIcon
                              color="success"
                              fontSize="small"
                            />
                          </Tooltip>
                        </InputAdornment>
                      ) : null,
                  }}
                />
                {formErrors.contactNumber && (
                  <FormHelperText error>
                    {formErrors.contactNumber}
                  </FormHelperText>
                )}
              </Grid>

              {/* Preferred Location Field */}
              <Grid item xs={12}>
                <ISelect
                  label="Preferred Location *"
                  options={indianCities}
                  value={formValues.preferredLocation}
                  onChange={(e, v) => handleInputChange("preferredLocation", v)}
                  error={!!formErrors.preferredLocation}
                  placeholder="Select your preferred city"
                />
                {formErrors.preferredLocation && (
                  <FormHelperText error>
                    {formErrors.preferredLocation}
                  </FormHelperText>
                )}
              </Grid>

              {/* Resume Upload Field */}
              <Grid item xs={12}>
                <Box>
                  <Button
                    variant="outlined"
                    component="label"
                    fullWidth
                    sx={{ mb: 1 }}
                  >
                    Upload Resume (Optional)
                    <input
                      hidden
                      type="file"
                      accept=".pdf,.docx"
                      onChange={handleFileChange}
                    />
                  </Button>
                  {formValues.resumeFile && (
                    <Typography variant="body2" sx={{ mt: 1, mb: 0.5 }}>
                      Selected file: {formValues.resumeFile.name}
                    </Typography>
                  )}
                  {formErrors.resumeFile && (
                    <FormHelperText error>
                      {formErrors.resumeFile}
                    </FormHelperText>
                  )}
                  <Typography variant="caption" color="text.secondary">
                    Supported formats: PDF, DOCX (Max size: 5MB)
                  </Typography>
                </Box>
              </Grid>

              {/* Comments Field */}
              <Grid item xs={12}>
                <TextField
                  label="Comments (Optional)"
                  placeholder="Add any additional comments here..."
                  multiline
                  rows={3}
                  fullWidth
                  value={formValues.comments}
                  onChange={(e) =>
                    handleInputChange("comments", e.target.value)
                  }
                  error={!!formErrors.comments}
                  inputProps={{ maxLength: 500 }}
                />
                <Box display="flex" justifyContent="space-between" mt={0.5}>
                  {formErrors.comments ? (
                    <FormHelperText error>{formErrors.comments}</FormHelperText>
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      Maximum 500 characters
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.secondary">
                    {formValues.comments.length}/500
                  </Typography>
                </Box>
              </Grid>

              {/* Action Buttons */}
              <Grid item xs={12}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      size="large"
                      disabled={Object.values(formErrors).some(
                        (error) => error !== "",
                      )}
                    >
                      Apply Now
                    </Button>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Button
                      fullWidth
                      variant="contained"
                      size="large"
                      sx={{
                        bgcolor: "#FF0000",
                        "&:hover": { bgcolor: "#CC0000" },
                      }}
                      onClick={handleClose}
                    >
                      Cancel
                    </Button>
                  </Grid>
                </Grid>
              </Grid>

              {/* Required Field Note */}
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">
                  * Required fields
                </Typography>
              </Grid>
            </Grid>
          </form>
        </Paper>
      </Box>
    </Container>
  );
};

export default CaregiverApplyForm;
// import React, { useState } from "react";
// import zIndex from "@mui/material/styles/zIndex";
// import {
//   Grid,
//   Box,
//   FormHelperText,
//   Container,
//   Paper,
//   Typography,
//   Button,
//   IconButton,
//   TextField,
//   MenuItem,
//   FormControl,
//   InputLabel,
//   InputAdornment,
// } from "@mui/material";
// import theme from "Theme";
// import ISelect from "../../components/SelectField";
// import CloseIcon from "@mui/icons-material/Close";
// import { useNavigate } from "react-router-dom";
// import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
// import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
// import ITimePicker from "../../components/ITimePicker";
// import { getNames } from "country-list";
// import countryTelephoneData from "country-telephone-data"; // Import the library
// import dayjs from "dayjs";
// import indianCities from "../../components/indianCities";
// import { useCreateInterestedClientMutation } from "services/clientApi";
// import { useLoading } from "../../components/LoadingProvider";
// import { useSnackbar } from "../../components/SnackbarProvider";
// import { useCreateInterestedCaregiverMutation } from "services/caregiverApi";
// import { CaregiverStatuses } from "CommonData/Common";

// const CaregiverApplyForm = () => {
//   const showSnackbar = useSnackbar();
//   const { startLoading, stopLoading } = useLoading();
//   const [minEndTime, setMinEndTime] = useState(null);
//   const [selectedFile, setSelectedFile] = useState(null);

//   const countryList = Array.from(
//     new Set(
//       countryTelephoneData.allCountries.map((country) =>
//         country.name.split("(")[0].trim()
//       )
//     )
//   );
//   const handleFileChange = (event) => {
//     setSelectedFile(event.target.files[0]);
//   };

//   function getPhoneCode(countryName) {
//     const country = countryTelephoneData.allCountries.find(
//       (country) => country.name.split("(")[0].trim() == countryName
//     );
//     return country ? country.dialCode : "";
//   }
//   const getMinEndTime = (value) => {
//     return value ? dayjs(value).add(30, "minute") : null;
//   };
//   const handleCountryTypeChange = (name, value) => {
//     handleInputChange(name, value);
//     handleInputChange("phoneCode", "+" + getPhoneCode(value) + "-");
//   };
//   const [countryType, setCountryType] = useState("");
//   const [serviceType, setServiceType] = useState("");
//   const [ic, setIc] = useState("");
//   const [createInteretedCG] = useCreateInterestedCaregiverMutation();

//   const initialFormValue = {
//     firstName: "",
//     lastName: "",
//     email: "",
//     experienceLevel: "",
//     qualityScore: "",
//     contactNumber: "",
//     preferredLocation: "",
//     availableFrom: null,
//     availableTo: null,
//     comments: "",
//     resumeFile: null,
//   }
//   const [formValues, setFormValues] = useState(initialFormValue);
//   const initialFormError = {
//     firstName: "",
//     lastName: "",
//     email: "",
//     // experienceLevel: "",
//     // qualityScore: "",
//     contactNumber: "",
//     preferredLocation: "",
//     // availableFrom: "",
//     // availableTo: "",
//     resumeFile: "",
//   }
//   const [formErrors, setFormErrors] = useState(initialFormError);

//   const handleInputChange = (name, value) => {
//     setFormValues((prevState) => ({
//       ...prevState,
//       [name]: value,
//     }));
//     setFormErrors((prevState) => ({
//       ...prevState,
//       [name]: "",
//     }));
//   };
//   const handleContactNumberChg = (name, value) => {
//     const numericValue = value.replace(/[^0-9]/g, "");

//     // Limit the input to 10 characters
//     if (numericValue.length <= 10) {
//       handleInputChange(name, numericValue);
//     }
//   };

//   const handleAvailabilityChge = (name, value) => {
//     handleInputChange(name, value);
//     handleInputChange("availableTo", dayjs(value).add(30, "minute"));
//     setMinEndTime(getMinEndTime(value));
//   };

//   const validateEmail = (email) => {
//     const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
//     return emailPattern.test(email);
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();

//     let errors = {
//       firstName: !formValues.firstName.trim() ? "Full Name is required" : "",
//       lastName: !formValues.lastName.trim() ? "Last Name is required" : "",
//       // qualityScore: !formValues.qualityScore.trim()
//       //   ? "Quality Score is required"
//       //   : "",
//       // experienceLevel: !formValues.experienceLevel.trim()
//       //   ? "Experience Level is required"
//       //   : "",
//       resumeFile: !formValues.resumeFile ? "Resume is required" : "",
//       email: !validateEmail(formValues.email) ? "Enter a valid email" : "",
//       contactNumber: !formValues.contactNumber.trim()
//       ? "Contact Number is required"
//       : formValues.contactNumber.trim().length !== 10
//       ? "Contact Number must be exactly 10 digits"
//       : "",
//       preferredLocation: !formValues.preferredLocation.trim()
//         ? "Service Location is required"
//         : "",
//       // availableFrom: !formValues.availableFrom?.isValid()
//       //   ? "Available Time is required"
//       //   : "",
//       // availableTo: !formValues.availableTo?.isValid()
//       //   ? "Available Time is required"
//       //   : "",
//     };

//     setFormErrors(errors);

//     const isFormValid = Object.values(errors).every((error) => error === "");

//     if (isFormValid) {
//       try {
//         startLoading();
//         // Send POST request to backend
//         const formData = new FormData();
//         formData.append("firstName", formValues.firstName);
//         formData.append("lastName", formValues.lastName);
//         formData.append("contactNumber", "+91-" + formValues.contactNumber);
//         formData.append("email", formValues.email);
//         // formData.append("experienceLevel", formValues.experienceLevel);
//         // formData.append("qualityScore", formValues.qualityScore);
//         formData.append('resumeFile', formValues.resumeFile);
//         formData.append("preferredLocation", formValues.preferredLocation);
//         // formData.append(
//         //   "availabilityStartTime",
//         //   formValues.availableFrom.format("HH:mm")
//         // );
//         // formData.append(
//         //   "availabilityEndTime",
//         //   formValues.availableTo.format("HH:mm")
//         // );

//         formData.append("comments", formValues.comments);
//         formData.append("status", CaregiverStatuses.INTERESTED.name);

//         const response = await createInteretedCG(formData).unwrap();
//         showSnackbar("Interest Sent for consideration", "success");
//         // Handle response if needed
//         //console.log('Response:', response);
//       } catch (error) {
//         showSnackbar(
//           "Couldn't send your interest. Please retry after sometime",
//           "error"
//         );
//         console.error("Error:", error);
//       } finally {
//         stopLoading();
//         handleClose();
//       }
//     } else {
//       // console.log("invalid form");
//       // console.log(formErrors);
//       // console.log(formValues);
//     }
//   };

//   const handleServiceTypeChange = (event) => {
//     setServiceType(event.target.value);
//   };
//   const shouldDisableEndTime = (timeValue) => {
//     if (!formValues.availableFrom) {
//       return false;
//     }
//     const minEndTime = dayjs(formValues.availableFrom).add(30, "minute");
//     return dayjs(timeValue).isBefore(minEndTime);
//   };

//   const style = {
//     backgroundColor: "white",
//     color: theme.palette.primary.main,
//     zIndex: 1,
//   };

//   const navigate = useNavigate();
//   const handleClose = () => {
//     setFormValues(initialFormValue)
//     setFormErrors(initialFormError)
//     navigate("/");
//   };

//   return (
//     <Container

//       maxWidth="md"
//       // sx={{
//       //   maxHeight: {
//       //     xs: "100vh", // Adjust as needed
//       //     sm: "none", // No max height for small and larger screens
//       //   },
//       //   overflowY: {
//       //     xs: "auto", // Enable vertical scroll for extra-small screens
//       //     sm: "visible", // Disable scroll for small and larger screens
//       //   },
//       // }}
//     >
//       <Box position="relative">
//         <Paper style={style} elevation={3} sx={{ borderRadius:5, padding: 2 }}>
//           <Typography variant="h5" gutterBottom>
//             Caregiver Application
//           </Typography>
//           <IconButton
//             onClick={handleClose}
//             sx={{
//               position: "absolute",
//               top: "5px",
//               right: "5px",
//               color: "red",
//               backgroundColor: "white",
//               zIndex: 1,
//             }}
//           >
//             <CloseIcon />
//           </IconButton>
//           <form onSubmit={handleSubmit}>
//             <Grid
//               container
//               justifyContent="center"
//               alignItems="center"
//               spacing={2}
//             >
//               <Grid item xs={12} sm={6}>
//                 <FormControl fullWidth>
//                   <TextField
//                     label="First Name"
//                     name="firstName"
//                     inputProps={{ maxLength: 35 }}
//                     value={formValues.firstName}
//                     onChange={(e) =>
//                       handleInputChange(e.target.name, e.target.value)
//                     }
//                     variant="outlined"
//                     required
//                   />
//                   {formErrors.firstName && (
//                     <FormHelperText sx={{ color: "red", height: "10px" }}>
//                       {formErrors.firstName}
//                     </FormHelperText>
//                   )}
//                 </FormControl>
//               </Grid>
//               <Grid item xs={12} sm={6}>
//                 <FormControl fullWidth>
//                   <TextField
//                     label="Last Name"
//                     name="lastName"
//                     inputProps={{ maxLength: 35 }}
//                     value={formValues.lastName}
//                     onChange={(e) =>
//                       handleInputChange(e.target.name, e.target.value)
//                     }
//                     variant="outlined"
//                     required
//                   />
//                   {formErrors.lastName && (
//                     <FormHelperText sx={{ color: "red", height: "10px" }}>
//                       {formErrors.lastName}
//                     </FormHelperText>
//                   )}
//                 </FormControl>
//               </Grid>
//               <Grid item xs={12} sm={4}>
//                 <FormControl fullWidth>
//                   <TextField
//                     label="Email"
//                     name="email"
//                     value={formValues.email}
//                     onChange={(e) =>
//                       handleInputChange(e.target.name, e.target.value)
//                     }
//                     variant="outlined"
//                     required
//                   />
//                   {formErrors.email && (
//                     <FormHelperText sx={{ color: "red", height: "10px" }}>
//                       {formErrors.email}
//                     </FormHelperText>
//                   )}
//                 </FormControl>
//               </Grid>
//               <Grid item xs={12} sm={4} className="custom-phone-input">
//                 <FormControl variant="outlined" fullWidth>
//                   <TextField
//                     InputProps={{
//                       startAdornment: (
//                         <InputAdornment
//                           position="start"
//                           sx={{ color: "white" }}
//                           color="primary"
//                         >
//                           <Box component="span" sx={{ color: "grey" }}>
//                             +91 -
//                           </Box>
//                         </InputAdornment>
//                       ),
//                     }}
//                     label="Contact Number"
//                     inputProps={{ maxLength: 10 }}
//                     name="contactNumber"
//                     value={formValues.contactNumber}
//                     onChange={(e) =>
//                       handleContactNumberChg(e.target.name, e.target.value)
//                     }
//                     variant="outlined"
//                     required
//                   />
//                   {formErrors.contactNumber && (
//                     <FormHelperText sx={{ color: "red", height: "10px" }}>
//                       {formErrors.contactNumber}
//                     </FormHelperText>
//                   )}
//                 </FormControl>
//               </Grid>

//               {/* <Grid item xs={12} sm={4}>
//                 <FormControl fullWidth>
//                   <TextField
//                     label="Experience Level"
//                     name="experienceLevel"
//                     value={formValues.experienceLevel}
//                     onChange={(e) =>
//                       handleInputChange(e.target.name, e.target.value)
//                     }
//                     variant="outlined"
//                     required
//                   />
//                   {formErrors.experienceLevel && (
//                     <FormHelperText sx={{ color: "red", height: "10px" }}>
//                       {formErrors.experienceLevel}
//                     </FormHelperText>
//                   )}
//                 </FormControl>
//               </Grid> */}

//               {/* <Grid item xs={12} sm={4}>
//                 <FormControl fullWidth>
//                   <TextField
//                     label="Quality Score"
//                     name="qualityScore"
//                     value={formValues.qualityScore}
//                     onChange={(e) =>
//                       handleInputChange(e.target.name, e.target.value)
//                     }
//                     variant="outlined"
//                     required
//                   />
//                   {formErrors.qualityScore && (
//                     <FormHelperText sx={{ color: "red", height: "10px" }}>
//                       {formErrors.qualityScore}
//                     </FormHelperText>
//                   )}
//                 </FormControl>
//               </Grid> */}
//               <Grid item xs={12} sm={4}>
//                 <FormControl fullWidth>
//                   <ISelect
//                     label="Preferred Location"
//                     name="preferredLocation"
//                     value={formValues.preferredLocation}
//                     options={indianCities}
//                     onChange={(e, newValue) =>
//                       handleInputChange("preferredLocation", newValue)
//                     }
//                     variant="outlined"
//                     required
//                   />
//                   {formErrors.preferredLocation && (
//                     <FormHelperText sx={{ color: "red", height: "10px" }}>
//                       {formErrors.preferredLocation}
//                     </FormHelperText>
//                   )}
//                 </FormControl>
//               </Grid>
//               {/* <LocalizationProvider dateAdapter={AdapterDayjs}>
//                 <Grid item xs={12} sm={4} fullWidth>
//                   <ITimePicker
//                     label="Availability (From)"
//                     name="availableFrom"
//                     value={formValues.availableFrom}
//                     onChange={(newValue) =>
//                       handleAvailabilityChge("availableFrom", newValue)
//                     }
//                   />
//                 </Grid>
//                 <Grid item xs={12} sm={4} fullWidth>
//                   <ITimePicker
//                     label="Availability (To)"
//                     name="availableTo"
//                     value={formValues.availableTo}
//                     minTime={minEndTime}
//                     onChange={(newValue) =>
//                       handleInputChange("availableTo", newValue)
//                     }
//                   />
//                 </Grid>
//               </LocalizationProvider> */}
//               <Grid item xs={12}>
//               <FormControl fullWidth>
//                 <Button
//                   variant="outlined"
//                   component="label"
//                 >
//                   Upload Resume
//                   <input
//                     type="file"
//                     name='resumeFile'
//                     hidden
//                     accept=".pdf,.docx"
//                     onChange={(e) => handleInputChange(e.target.name, e.target.files[0])}
//                     />
//                 </Button>
//                 {formValues.resumeFile && <Typography variant="body2">{formValues.resumeFile?.name}</Typography>}
//                 {formErrors.resumeFile &&
//                 <FormHelperText sx={{ color: 'red', height: '10px' }}>{formErrors.resumeFile}</FormHelperText>
//                 }
//               </FormControl>
//             </Grid>

//               <Grid item xs={12}>
//                 <TextField
//                   label="Comments (If Any)"
//                   multiline
//                   rows={2}
//                   onChange={(e) =>
//                     handleInputChange("comments", e.target.value)
//                   }
//                   variant="outlined"
//                   fullWidth
//                 />
//               </Grid>
//               <Grid
//                 item
//                 sm={6}
//                 style={{ display: "flex", justifyContent: "flex-end" }}
//               >
//                 <Button
//                   variant="contained"
//                   color="primary"
//                   onClick={handleSubmit}
//                 >
//                   Apply
//                 </Button>
//               </Grid>
//               <Grid item sm={6}>
//                 <Button
//                   variant="contained"
//                   sx={{
//                     bgcolor: "#FF0000",
//                     '&:hover': {
//                       bgcolor: "#CC0000", // Darker shade of red for hover effect
//                     },
//                   }}
//                   onClick={handleClose}
//                 >
//                   Cancel
//                 </Button>
//               </Grid>
//             </Grid>
//           </form>
//         </Paper>
//       </Box>
//     </Container>
//   );
// };

// export default CaregiverApplyForm;
